import type {PropertySearchFilters, PropertySearchSnapshot} from "@/hooks/usePropertySearch"
import type {RegionCode} from "@casas-diamante/types"

const PROPERTY_SEARCH_CACHE_KEY = "property-search-cache-v2"
const PROPERTY_SEARCH_CACHE_TTL_MS = 15 * 60 * 1000

interface StoredPropertySearchSnapshot extends PropertySearchSnapshot {
   savedAt: number
}

const isStoredPropertySearchSnapshot = (
   value: unknown,
): value is StoredPropertySearchSnapshot => {
   if (!value || typeof value !== "object") return false

   const snapshot = value as Partial<StoredPropertySearchSnapshot>
   const filters = snapshot.filters as Partial<PropertySearchFilters> | undefined

   return (
      Array.isArray(snapshot.properties) &&
      typeof snapshot.page === "number" &&
      typeof snapshot.totalHits === "number" &&
      typeof snapshot.noMoreInm === "boolean" &&
      typeof snapshot.savedAt === "number" &&
      Number.isFinite(snapshot.savedAt) &&
      !!filters &&
      typeof filters.region === "string" &&
      typeof filters.searchString === "string" &&
      Array.isArray(filters.partners) &&
      (filters.suspended === null || typeof filters.suspended === "string") &&
      (filters.available === null || typeof filters.available === "string") &&
      Array.isArray(filters.status) &&
      Array.isArray(filters.condoClass) &&
      (filters.scheme === null || filters.scheme === "sale" || filters.scheme === "rent")
   )
}

export const readPropertySearchCache = (): PropertySearchSnapshot | null => {
   const storedCache = localStorage.getItem(PROPERTY_SEARCH_CACHE_KEY)
   if (!storedCache) return null

   try {
      const snapshot: unknown = JSON.parse(storedCache)
      if (isStoredPropertySearchSnapshot(snapshot)) {
         const ageMs = Date.now() - snapshot.savedAt
         if (ageMs >= 0 && ageMs < PROPERTY_SEARCH_CACHE_TTL_MS) return snapshot
      }
   } catch {
      // Invalid local data is discarded so it cannot block a new search.
   }

   localStorage.removeItem(PROPERTY_SEARCH_CACHE_KEY)
   return null
}

export const savePropertySearchCache = (snapshot: PropertySearchSnapshot) =>
   localStorage.setItem(
      PROPERTY_SEARCH_CACHE_KEY,
      JSON.stringify({...snapshot, savedAt: Date.now()}),
   )

export const clearPropertySearchCache = () => localStorage.removeItem(PROPERTY_SEARCH_CACHE_KEY)

export const propertySearchCacheMatchesRegion = (
   snapshot: PropertySearchSnapshot,
   region: RegionCode,
) => snapshot.filters.region === region
